import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PageShell } from "../components/Layout";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Robot, UserCircle, EyeSlash, Eye, Warning } from "@phosphor-icons/react";

/**
 * Super-Admin · Astrologer Visibility
 * ───────────────────────────────────
 * Two kill-switches that control what shows up on the public /astrologers
 * browse page:
 *   • AI Astrologers  ON/OFF
 *   • Human Astrologers  ON/OFF
 *
 * Use cases:
 *  - Pause AI cohort while moderating prompts
 *  - "Human-only weekend" promotion
 *  - Hide humans during off-hours / staffing gaps
 *
 * Either toggle takes effect immediately — the next page load on
 * /astrologers reflects the change (no deploy required).
 */
export default function AdminAstrologerVisibilityPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showAi, setShowAi] = useState(true);
  const [showHuman, setShowHuman] = useState(true);
  // Per-test-user overrides — only impact the dedicated QA email
  // ("Astrologykuber@gmail.com"). Everyone else still gets the global
  // flags above. Lets us QA the human-astrologer flow while humans are
  // globally hidden, or vice-versa.
  const [testEmail, setTestEmail] = useState("");
  const [testAi, setTestAi] = useState(true);
  const [testHuman, setTestHuman] = useState(true);
  const [meta, setMeta] = useState({ updated_at: null, updated_by: null });

  useEffect(() => {
    if (user === null) navigate("/");
    if (user && !user.is_super_admin) navigate("/");
  }, [user, navigate]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/super-admin/astrologer-visibility");
        setShowAi(data.show_ai);
        setShowHuman(data.show_human);
        setTestEmail(data.test_user_email || "");
        setTestAi(data.test_user_show_ai ?? data.show_ai);
        setTestHuman(data.test_user_show_human ?? data.show_human);
        setMeta({ updated_at: data.updated_at, updated_by: data.updated_by });
      } catch {
        toast.error("Couldn't load current settings");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const save = async (next) => {
    setSaving(true);
    try {
      // Always send full payload so partial saves don't reset the other
      // switches. `next` may carry any subset of the four toggles.
      const payload = {
        show_ai: next.show_ai ?? showAi,
        show_human: next.show_human ?? showHuman,
        test_user_show_ai: next.test_user_show_ai ?? testAi,
        test_user_show_human: next.test_user_show_human ?? testHuman,
      };
      const { data } = await api.post("/super-admin/astrologer-visibility", payload);
      setShowAi(data.show_ai);
      setShowHuman(data.show_human);
      if (typeof data.test_user_show_ai === "boolean") setTestAi(data.test_user_show_ai);
      if (typeof data.test_user_show_human === "boolean") setTestHuman(data.test_user_show_human);
      toast.success("Visibility updated — live on /astrologers now.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const bothOff = !showAi && !showHuman;

  if (loading) {
    return (
      <PageShell>
        <div className="max-w-2xl mx-auto py-12 px-4 text-muted">Loading…</div>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <div className="max-w-2xl mx-auto py-12 px-4">
        <p className="text-[10px] uppercase tracking-widest font-bold text-amber-700">
          Super Admin · Visibility kill switches
        </p>
        <h1 className="font-serif-display text-3xl mt-1 mb-2">
          Astrologer visibility
        </h1>
        <p className="text-sm text-muted mb-6">
          Hide or show the entire AI or Human astrologer cohort on the public{" "}
          <span className="font-semibold">/astrologers</span> browse page.
          Takes effect immediately on the next page load.
        </p>

        {bothOff && (
          <div className="mb-5 rounded-xl p-3.5 border-2 border-red-300 bg-red-50 text-red-900 flex items-start gap-2" data-testid="both-off-warning">
            <Warning size={18} weight="fill" className="shrink-0 mt-0.5" />
            <div className="text-xs leading-relaxed">
              <span className="font-bold">Heads up:</span> both cohorts are
              hidden — your /astrologers page is empty. Re-enable at least one
              before public users notice.
            </div>
          </div>
        )}

        <div className="space-y-4">
          <ToggleRow
            label="AI Astrologers"
            description="MessengerofShiva, Amelia, Savannah, Ammu, and the rest of the AI cohort."
            icon={Robot}
            on={showAi}
            disabled={saving}
            onChange={(v) => save({ show_ai: v })}
            testid="toggle-ai"
          />
          <ToggleRow
            label="Human Astrologers"
            description="Sachin, Priya, and any other verified human astrologers."
            icon={UserCircle}
            on={showHuman}
            disabled={saving}
            onChange={(v) => save({ show_human: v })}
            testid="toggle-human"
          />
        </div>

        {/* Per-test-user override — only affects the single QA email below.
            Everyone else continues to see whatever the global switches above
            decide. Use this to QA the human-astrologer cohort while it's
            globally hidden, or vice-versa. */}
        <div className="mt-10 rounded-2xl border border-amber-300 bg-amber-50/60 p-5" data-testid="test-user-override-section">
          <div className="flex items-start justify-between gap-3 mb-1">
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-widest font-bold text-amber-700">
                QA override · single test account
              </p>
              <h2 className="font-serif-display text-xl mt-1">
                Visibility for{" "}
                <span className="font-mono text-base text-amber-900 break-all" data-testid="test-user-email">
                  {testEmail || "—"}
                </span>
              </h2>
            </div>
          </div>
          <p className="text-xs text-amber-900/80 mb-4 leading-relaxed">
            These switches ONLY affect the directory view served to{" "}
            <span className="font-semibold">{testEmail || "the configured test account"}</span>{" "}
            when signed in. Everyone else still sees whatever the global
            switches above decide. Lets the team test chats/calls with humans
            (or AI) without exposing the cohort to all users.
          </p>

          <div className="space-y-3">
            <ToggleRow
              label="AI Astrologers · QA view"
              description="Override AI visibility for the test account only."
              icon={Robot}
              on={testAi}
              disabled={saving}
              onChange={(v) => save({ test_user_show_ai: v })}
              testid="toggle-test-ai"
            />
            <ToggleRow
              label="Human Astrologers · QA view"
              description="Override human visibility for the test account only."
              icon={UserCircle}
              on={testHuman}
              disabled={saving}
              onChange={(v) => save({ test_user_show_human: v })}
              testid="toggle-test-human"
            />
          </div>
        </div>

        {meta.updated_at && (
          <p className="text-[11px] text-muted mt-6">
            Last changed by{" "}
            <span className="font-semibold">{meta.updated_by || "—"}</span>
            {" · "}
            {new Date(meta.updated_at).toLocaleString()}
          </p>
        )}
      </div>
    </PageShell>
  );
}

function ToggleRow({ label, description, icon: Icon, on, disabled, onChange, testid }) {
  return (
    <div
      className={`rounded-2xl p-4 border-2 transition-colors ${
        on ? "border-emerald-400 bg-emerald-50/60" : "border-zinc-300 bg-zinc-50"
      }`}
      data-testid={testid}
    >
      <div className="flex items-center gap-3">
        <div
          className={`rounded-full p-2.5 shrink-0 ${
            on ? "bg-emerald-500 text-white" : "bg-zinc-400 text-white"
          }`}
        >
          <Icon size={20} weight="duotone" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-base">{label}</h3>
          <p className="text-xs text-muted">{description}</p>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={on}
          onClick={() => onChange(!on)}
          disabled={disabled}
          data-testid={`${testid}-switch`}
          className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors disabled:opacity-50 ${
            on ? "bg-emerald-500" : "bg-zinc-300"
          }`}
        >
          <span
            className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
              on ? "translate-x-6" : "translate-x-1"
            }`}
          />
        </button>
      </div>
      <div className={`mt-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold ${on ? "text-emerald-700" : "text-zinc-500"}`}>
        {on ? <Eye size={11} weight="fill" /> : <EyeSlash size={11} weight="fill" />}
        {on ? "Visible on /astrologers" : "Hidden from /astrologers"}
      </div>
    </div>
  );
}
